import type { DnsForwardingConfig } from 'Types/subscription.types';
import type {
  DnsRule,
  RuleValidationError,
} from '../components/dns-forwarding-editor/DnsForwardingEditor.types';

let nextRuleId = 0;
export const getNextRuleId = (): number => nextRuleId++;

export const ipv4RegEx =
  /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;

export const ipv6RegEx =
  /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^(([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4})?::([0-9a-fA-F]{1,4}(:[0-9a-fA-F]{1,4}){0,6})?$/;

export const isValidIp = (ip: string): boolean => ipv4RegEx.test(ip) || ipv6RegEx.test(ip);

export const domainRegEx = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

export const configToRules = (config: DnsForwardingConfig): DnsRule[] =>
  Object.keys(config).map((domain) => ({
    id: getNextRuleId(),
    domain,
    ips: [...config[domain].forward],
  }));

export const rulesToConfig = (rules: DnsRule[]): DnsForwardingConfig =>
  rules.reduce<DnsForwardingConfig>((config, rule) => {
    const ips = rule.ips.map((ip) => ip.trim()).filter((ip) => ip !== '');

    if (rule.domain.trim() !== '' && ips.length > 0) {
      config[rule.domain.trim()] = { forward: ips };
    }

    return config;
  }, {});

export const validateRule = (
  rule: DnsRule,
  allRules: DnsRule[],
  ruleIndex: number
): RuleValidationError | null => {
  const domain = rule.domain.trim();

  const isDuplicate = allRules.some(
    (r, i) => i !== ruleIndex && r.domain.trim().toLowerCase() === domain.toLowerCase()
  );
  const hasInvalidIp = rule.ips.some((ip) => ip.trim() !== '' && !isValidIp(ip.trim()));

  const domainError =
    domain === ''
      ? 'Domain is required'
      : !domainRegEx.test(domain)
        ? 'Invalid domain format'
        : isDuplicate
          ? 'Duplicate domain'
          : undefined;

  const ipsError =
    rule.ips.length === 0
      ? 'At least one DNS server IP is required'
      : hasInvalidIp
        ? 'Invalid IP address'
        : undefined;

  const errors: RuleValidationError = {
    ...(domainError !== undefined && { domain: domainError }),
    ...(ipsError !== undefined && { ips: ipsError }),
  };

  return Object.keys(errors).length > 0 ? errors : null;
};
